import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Form, Modal, Spinner } from "react-bootstrap";
import { AppDispatch, RootState } from "../../store";
import { fetchBmsBookings } from "../../features/bms/bmsSlice";
import bmsService, {
  BmsBooking, BmsCalendarEvent, BmsCalendarResource, BmsCustomer,
  BmsServiceItem, BmsUser,
} from "../../features/bms/bmsService";
import { TbCalendarEvent, TbEdit, TbFilter, TbPlus, TbX, TbCheck, TbClock, TbSearch, TbUserPlus, TbUser } from "react-icons/tb";
import BmsChatbotSelector from "../../components/bms/BmsChatbotSelector";
import BmsPagination from "../../components/bms/BmsPagination";
import DateRangePicker from "../../components/common/DateRangePicker";
import { showError, showSuccess } from "../../utils/bmsToast";

const DEFAULT_TZ = "Europe/Istanbul";
const PAGE_SIZE = 25;

type SlotStatus = "available" | "booked" | "off";

interface Slot {
  startMin: number;
  endMin: number;
  label: string;
  status: SlotStatus;
}

function toDatetimeLocalValue(iso: string | undefined): string {
  if (!iso) return "";
  return iso.replace(" ", "T").slice(0, 16);
}

function fromDatetimeLocalValue(v: string): string {
  return v.replace("T", " ");
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function todayIso(): string {
  return toIsoDate(new Date());
}

function minutesToHHMM(m: number): string {
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
}

function parseTimeToMinutes(val: string | null | undefined): number | null {
  if (!val) return null;
  const parts = val.split(":").map(Number);
  if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
  return parts[0] * 60 + parts[1];
}

// Parse "YYYY-MM-DDTHH:MM:SS" (or space-separated) into minutes-of-day for a given date.
// Returns null if the timestamp belongs to a different date.
function eventMinutesForDate(ts: string, isoDate: string): number | null {
  if (!ts) return null;
  const normalized = ts.replace(" ", "T");
  if (!normalized.startsWith(isoDate)) return null;
  const timePart = normalized.slice(11, 16);
  return parseTimeToMinutes(timePart);
}

function formatTimeRange(startMin: number, endMin: number): string {
  return `${minutesToHHMM(startMin)} – ${minutesToHHMM(endMin)}`;
}

const STATUS_COLORS: Record<string, string> = {
  confirmed: "active",
  pending: "warning",
  cancelled: "inactive",
  completed: "completed",
};

const STATUS_OPTIONS_CREATE = ["confirmed", "pending", "cancelled", "completed"] as const;

const statusInfoCreate = (s: string, t: (k: string) => string) => {
  switch (s) {
    case "confirmed": return { cls: "confirmed", label: t("bms_confirmed") };
    case "pending": return { cls: "pending", label: t("bms_pending") };
    case "cancelled": return { cls: "cancelled", label: t("bms_cancelled") };
    case "completed": return { cls: "completed", label: t("bms_completed") };
    default: return { cls: "", label: s };
  }
};

const BmsBookingsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { bookings, bookingsTotal, isLoading, error } = useSelector((s: RootState) => s.bms);
  const [chatbotId, setChatbotId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [textFilter, setTextFilter] = useState("");
  const [serviceFilterId, setServiceFilterId] = useState<number | null>(null);
  const [serviceFilterSearch, setServiceFilterSearch] = useState("");
  const [showServiceFilterDropdown, setShowServiceFilterDropdown] = useState(false);
  const serviceFilterRef = React.useRef<HTMLDivElement>(null);
  const [customerFilterId, setCustomerFilterId] = useState<number | null>(null);
  const [customerFilterSearch, setCustomerFilterSearch] = useState("");
  const [showCustomerFilterDropdown, setShowCustomerFilterDropdown] = useState(false);
  const customerFilterRef = React.useRef<HTMLDivElement>(null);
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(PAGE_SIZE);

  const [users, setUsers] = useState<BmsUser[]>([]);
  const [services, setServices] = useState<BmsServiceItem[]>([]);
  const [customers, setCustomers] = useState<BmsCustomer[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState<BmsBooking | null>(null);
  const [saving, setSaving] = useState(false);

  const [createForm, setCreateForm] = useState({
    resource_user_id: 0, service_id: 0,
    customer_name: "", customer_surname: "", customer_phone: "", customer_email: "",
    start_at: "", end_at: "", timezone: DEFAULT_TZ,
  });
  const [createStatus, setCreateStatus] = useState<string>("confirmed");
  const [bookingDate, setBookingDate] = useState<string>(todayIso());
  const [selectedStartMin, setSelectedStartMin] = useState<number | null>(null);
  const [calendarResource, setCalendarResource] = useState<BmsCalendarResource | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Customer mode for create modal
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [selectedCustomerId2, setSelectedCustomerId2] = useState<number | null>(null);
  const [customerSearch2, setCustomerSearch2] = useState("");
  const [showDropdown2, setShowDropdown2] = useState(false);
  const searchRef2 = React.useRef<HTMLDivElement>(null);
  const [serviceSearchCreate, setServiceSearchCreate] = useState("");
  const [showServiceDropdownCreate, setShowServiceDropdownCreate] = useState(false);
  const serviceSearchRefCreate = React.useRef<HTMLDivElement>(null);

  const [editForm, setEditForm] = useState({
    status: "confirmed", resource_user_id: 0, service_id: 0, customer_id: 0,
    start_at: "", end_at: "", timezone: DEFAULT_TZ, cancellation_reason: "",
  });
  const [editIsNewCustomer, setEditIsNewCustomer] = useState(false);
  const [editSelectedCustomerId, setEditSelectedCustomerId] = useState<number | null>(null);
  const [editCustomerSearch, setEditCustomerSearch] = useState("");
  const [editShowDropdown, setEditShowDropdown] = useState(false);
  const [editCustomerName, setEditCustomerName] = useState("");
  const [editCustomerSurname, setEditCustomerSurname] = useState("");
  const [editCustomerPhone, setEditCustomerPhone] = useState("");
  const [editCustomerEmail, setEditCustomerEmail] = useState("");
  const [editServiceSearch, setEditServiceSearch] = useState("");
  const [editShowServiceDropdown, setEditShowServiceDropdown] = useState(false);
  const editServiceSearchRef = React.useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    if (!chatbotId) return;
    dispatch(fetchBmsBookings({
      chatbotId,
      params: {
        status: statusFilter || undefined,
        q: textFilter.trim() || undefined,
        service_id: serviceFilterId || undefined,
        customer_phone: customerFilterId ? (customers.find((c) => c.id === customerFilterId)?.phone || undefined) : undefined,
        date_from: dateFromFilter || undefined,
        date_to: dateToFilter || undefined,
        limit,
        skip,
      },
    }));
  }, [chatbotId, statusFilter, textFilter, serviceFilterId, customerFilterId, dateFromFilter, dateToFilter, limit, skip, dispatch]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (error) showError(error); }, [error]);
  useEffect(() => { setSkip(0); }, [chatbotId, statusFilter, textFilter, serviceFilterId, customerFilterId, dateFromFilter, dateToFilter]);

  useEffect(() => {
    const loadRefs = async () => {
      if (!chatbotId) { setUsers([]); setServices([]); setCustomers([]); return; }
      setLoadingRefs(true);
      try {
        const [uRes, sRes, cRes] = await Promise.all([
          bmsService.listUsers(chatbotId, { limit: 200 }),
          bmsService.listServices(chatbotId, { limit: 200 }),
          bmsService.listCustomers(chatbotId, { limit: 200 }),
        ]);
        const uList = (uRes.data || []).filter((u) => u.status === "active");
        const sList = (sRes.data || []).filter((s) => !!s.is_active);
        setUsers(uList);
        setServices(sList);
        setCustomers(cRes.data || []);
        if (uList.length) setCreateForm((f) => ({ ...f, resource_user_id: uList[0].id }));
        if (sList.length) setCreateForm((f) => ({ ...f, service_id: sList[0].id }));
      } catch (e: any) {
        showError(e?.response?.data?.detail || e?.message || "Error");
      } finally {
        setLoadingRefs(false);
      }
    };
    loadRefs();
  }, [chatbotId]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (serviceFilterRef.current && !serviceFilterRef.current.contains(e.target as Node)) {
        setShowServiceFilterDropdown(false);
      }
      if (customerFilterRef.current && !customerFilterRef.current.contains(e.target as Node)) {
        setShowCustomerFilterDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const openCreate = () => {
    setCreateForm((f) => ({
      ...f,
      customer_name: "", customer_surname: "", customer_phone: "", customer_email: "",
      start_at: "", end_at: "", timezone: DEFAULT_TZ,
      resource_user_id: users[0]?.id || 0, service_id: services[0]?.id || 0,
    }));
    setBookingDate(todayIso());
    setSelectedStartMin(null);
    setCalendarResource(null);
    setIsNewCustomer(false);
    setSelectedCustomerId2(null);
    setCustomerSearch2("");
    setServiceSearchCreate("");
    setShowServiceDropdownCreate(false);
    setCreateStatus("confirmed");
    setShowCreate(true);
  };

  // Load availability + existing bookings for the selected expert & date
  useEffect(() => {
    const loadSlots = async () => {
      if (!showCreate || !chatbotId || !createForm.resource_user_id || !bookingDate) {
        setCalendarResource(null);
        return;
      }
      setLoadingSlots(true);
      try {
        const res = await bmsService.getCalendar(chatbotId, {
          start_at: `${bookingDate}T00:00:00`,
          end_at: `${bookingDate}T23:59:59`,
        });
        const resource = (res.resources || []).find(
          (r) => r.resource.resource_user_id === createForm.resource_user_id
        );
        setCalendarResource(resource || null);
      } catch (e: any) {
        showError(e?.response?.data?.detail || e?.message || "Error");
        setCalendarResource(null);
      } finally {
        setLoadingSlots(false);
      }
    };
    loadSlots();
    setSelectedStartMin(null);
  }, [showCreate, chatbotId, createForm.resource_user_id, bookingDate]);

  const slotDuration = useMemo(() => {
    const svc = services.find((s) => s.id === createForm.service_id);
    return svc?.duration_minutes && svc.duration_minutes > 0 ? svc.duration_minutes : 30;
  }, [services, createForm.service_id]);

  const slotData = useMemo<{ slots: Slot[]; hasAvailability: boolean }>(() => {
    if (!calendarResource) return { slots: [], hasAvailability: false };
    const dayOfWeek = new Date(`${bookingDate}T00:00:00`).getDay();
    const applicable = (calendarResource.availability || []).filter((a) =>
      Array.isArray(a.days) && a.days.includes(dayOfWeek)
    );

    // Build list of "working" minute ranges and "break" minute ranges
    const workingRanges: Array<[number, number]> = [];
    const breakRanges: Array<[number, number]> = [];
    for (const a of applicable) {
      const ws = parseTimeToMinutes(a.start_at);
      const we = parseTimeToMinutes(a.end_at);
      if (ws !== null && we !== null && we > ws) workingRanges.push([ws, we]);
      const bs = parseTimeToMinutes(a.break_start_at ?? undefined);
      const be = parseTimeToMinutes(a.break_end_at ?? undefined);
      if (bs !== null && be !== null && be > bs) breakRanges.push([bs, be]);
    }

    // No availability for this day → no slots (grid stays empty, UI shows hint)
    if (workingRanges.length === 0) {
      return { slots: [], hasAvailability: false };
    }

    // Derive slot window from the expert's actual working hours for this day.
    // Use the earliest start and the latest end across all applicable windows,
    // aligned to the slot duration.
    const minStart = Math.min(...workingRanges.map((r) => r[0]));
    const maxEnd = Math.max(...workingRanges.map((r) => r[1]));
    const alignedStart = Math.floor(minStart / slotDuration) * slotDuration;
    const alignedEnd = Math.ceil(maxEnd / slotDuration) * slotDuration;

    // Build list of booked ranges (ignore cancelled bookings)
    const bookedRanges: Array<[number, number]> = [];
    for (const ev of (calendarResource.events || []) as BmsCalendarEvent[]) {
      if (ev.status === "cancelled") continue;
      const s = eventMinutesForDate(ev.start_at, bookingDate);
      const e = eventMinutesForDate(ev.end_at, bookingDate);
      if (s !== null && e !== null && e > s) bookedRanges.push([s, e]);
    }

    const overlapsAny = (a: number, b: number, ranges: Array<[number, number]>) =>
      ranges.some(([rs, re]) => a < re && b > rs);

    const containedInAny = (a: number, b: number, ranges: Array<[number, number]>) =>
      ranges.some(([rs, re]) => a >= rs && b <= re);

    const result: Slot[] = [];
    for (let m = alignedStart; m + slotDuration <= alignedEnd; m += slotDuration) {
      const endM = m + slotDuration;
      let status: SlotStatus;
      if (!containedInAny(m, endM, workingRanges) || overlapsAny(m, endM, breakRanges)) {
        status = "off";
      } else if (overlapsAny(m, endM, bookedRanges)) {
        status = "booked";
      } else {
        status = "available";
      }
      result.push({ startMin: m, endMin: endM, label: minutesToHHMM(m), status });
    }
    return { slots: result, hasAvailability: true };
  }, [calendarResource, bookingDate, slotDuration]);

  const slots = slotData.slots;
  const hasAvailability = slotData.hasAvailability;

  const handleSelectSlot = (slot: Slot) => {
    if (slot.status !== "available") return;
    setSelectedStartMin(slot.startMin);
    const startDt = `${bookingDate}T${minutesToHHMM(slot.startMin)}`;
    const endDt = `${bookingDate}T${minutesToHHMM(slot.endMin)}`;
    setCreateForm((f) => ({ ...f, start_at: startDt, end_at: endDt }));
  };

  const openEdit = (row: BmsBooking) => {
    const fallbackCustomer = customers.find((c) => {
      const samePhone = !!row.customer_phone && c.phone === row.customer_phone;
      const sameName = `${c.name} ${c.surname}`.trim().toLowerCase() === `${row.customer_name || ""} ${row.customer_surname || ""}`.trim().toLowerCase();
      return samePhone || sameName;
    });
    const initialCustomerId = row.customer_id || fallbackCustomer?.id || 0;
    setEditing(row);
    setEditForm({
      status: row.status || "confirmed",
      resource_user_id: row.resource_user_id,
      service_id: row.service_id || services[0]?.id || 0,
      customer_id: initialCustomerId,
      start_at: toDatetimeLocalValue(row.start_at),
      end_at: toDatetimeLocalValue(row.end_at),
      timezone: row.timezone || DEFAULT_TZ,
      cancellation_reason: row.cancellation_reason || "",
    });
    setEditIsNewCustomer(false);
    setEditSelectedCustomerId(initialCustomerId || null);
    setEditCustomerSearch("");
    setEditShowDropdown(false);
    setEditCustomerName("");
    setEditCustomerSurname("");
    setEditCustomerPhone("");
    setEditCustomerEmail("");
    setEditServiceSearch("");
    setEditShowServiceDropdown(false);
    setShowEdit(true);
  };

  const handleCreate = async () => {
    if (!chatbotId) return;
    let cName = createForm.customer_name.trim();
    let cSurname = createForm.customer_surname.trim();
    let cPhone = createForm.customer_phone.trim();
    let cEmail = createForm.customer_email.trim();
    if (!isNewCustomer && selectedCustomerId2) {
      const c = customers.find((x) => x.id === selectedCustomerId2);
      if (c) { cName = c.name; cSurname = c.surname; cPhone = c.phone; cEmail = c.email || ""; }
    }
    if (!cName || !cPhone) { showError(t("bms_invalid_customer")); return; }
    if (!createForm.service_id) { showError(t("bms_select_service_booking")); return; }
    if (!createForm.start_at || !createForm.end_at) { showError(t("bms_invalid_datetime")); return; }
    setSaving(true);
    try {
      const res = await bmsService.createBooking(chatbotId, {
        resource_user_id: createForm.resource_user_id, service_id: createForm.service_id,
        customer_name: cName, customer_surname: cSurname,
        customer_phone: cPhone, customer_email: cEmail || undefined,
        start_at: fromDatetimeLocalValue(createForm.start_at), end_at: fromDatetimeLocalValue(createForm.end_at),
        timezone: createForm.timezone.trim() || DEFAULT_TZ,
        status: createStatus,
      });
      showSuccess(`${t("bms_booking_created")} ${res.booking_number}`);
      setShowCreate(false);
      load();
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Error");
    } finally { setSaving(false); }
  };

  const handleEditSave = async () => {
    if (!chatbotId || !editing) return;
    if (!editForm.service_id) { showError(t("bms_select_service_booking")); return; }
    let resolvedCustomerId = editSelectedCustomerId || 0;
    if (editIsNewCustomer) {
      const n = editCustomerName.trim().toLowerCase();
      const s = editCustomerSurname.trim().toLowerCase();
      const p = editCustomerPhone.trim();
      const exact = customers.find((c) =>
        c.phone === p &&
        c.name.trim().toLowerCase() === n &&
        c.surname.trim().toLowerCase() === s
      );
      if (!exact) {
        showError(t("bms_invalid_customer"));
        return;
      }
      resolvedCustomerId = exact.id;
    }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        status: editForm.status, resource_user_id: editForm.resource_user_id,
        service_id: editForm.service_id,
        start_at: fromDatetimeLocalValue(editForm.start_at), end_at: fromDatetimeLocalValue(editForm.end_at),
        timezone: editForm.timezone.trim() || DEFAULT_TZ,
      };
      if (resolvedCustomerId && resolvedCustomerId !== editing.customer_id) payload.customer_id = resolvedCustomerId;
      if (editForm.status === "cancelled" && editForm.cancellation_reason.trim()) payload.cancellation_reason = editForm.cancellation_reason.trim();
      await bmsService.updateBooking(chatbotId, editing.id, payload);
      showSuccess(t("bms_booking_updated"));
      setShowEdit(false); setEditing(null);
      load();
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || "Error");
    } finally { setSaving(false); }
  };

  const activeUsers = users.filter((u) => u.status === "active");
  const activeServices = services.filter((s) => !!s.is_active);
  const dateRangePresets = useMemo(() => ([
    {
      key: "today",
      label: t("today"),
      getRange: () => {
        const end = new Date();
        const start = new Date(end);
        return { start, end };
      },
    },
    {
      key: "7d",
      label: t("last_7_days"),
      getRange: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 6);
        return { start, end };
      },
    },
    {
      key: "30d",
      label: t("last_30_days"),
      getRange: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 29);
        return { start, end };
      },
    },
  ]), [t]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (serviceSearchRefCreate.current && !serviceSearchRefCreate.current.contains(e.target as Node)) {
        setShowServiceDropdownCreate(false);
      }
      if (editServiceSearchRef.current && !editServiceSearchRef.current.contains(e.target as Node)) {
        setEditShowServiceDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="chatbots-page bms-page">
      <div className="page-header-card mb-3">
        <div className="page-header-text">
          <h1 className="d-flex align-items-center gap-2"><TbCalendarEvent /> {t("bms_bookings")}</h1>
          <p>{t("bms_bookings_desc")}</p>
        </div>
      </div>

      <div className="p-3 pt-0">
        <BmsChatbotSelector chatbotId={chatbotId} onChatbotChange={setChatbotId} />

        <div className="bms-bookings-actions mt-3">
          <div className="bms-bookings-actions-left">
            <span className="bms-section-subtitle">{t("filters")}</span>
          </div>
          <button className="bms-btn-primary" onClick={openCreate} disabled={!chatbotId || activeUsers.length === 0 || activeServices.length === 0}>
            <TbPlus /> {t("bms_new_booking")}
          </button>
        </div>

        <div className="bms-toolbar mt-2">
          <div className="bms-bookings-filter-grid">
            <div className="bms-customer-search-wrap bms-filter-col bms-filter-col-wide">
              <div className="bms-customer-search-input">
                <TbSearch className="bms-cs-icon" />
                <input
                  type="text"
                  className="bms-cs-field"
                  placeholder={t("search")}
                  value={textFilter}
                  onChange={(e) => setTextFilter(e.target.value)}
                />
                {textFilter && (
                  <button type="button" className="bms-cs-clear" onClick={() => setTextFilter("")}><TbX size={14} /></button>
                )}
              </div>
            </div>

            <div className="bms-customer-search-wrap bms-filter-col" ref={serviceFilterRef}>
              <div className="bms-customer-search-input">
                <TbSearch className="bms-cs-icon" />
                <input
                  type="text"
                  className="bms-cs-field"
                  placeholder={t("bms_select_service_booking")}
                  value={serviceFilterId ? (() => {
                    const s = activeServices.find((x) => x.id === serviceFilterId);
                    return s ? `${s.name} (${s.duration_minutes} ${t("bms_minutes_short")})` : serviceFilterSearch;
                  })() : serviceFilterSearch}
                  onChange={(e) => {
                    setServiceFilterSearch(e.target.value);
                    setServiceFilterId(null);
                    setShowServiceFilterDropdown(true);
                  }}
                  onFocus={() => setShowServiceFilterDropdown(true)}
                />
                {serviceFilterId && (
                  <button type="button" className="bms-cs-clear" onClick={() => { setServiceFilterId(null); setServiceFilterSearch(""); setShowServiceFilterDropdown(true); }}><TbX size={14} /></button>
                )}
              </div>
              {showServiceFilterDropdown && !serviceFilterId && (
                <div className="bms-customer-dropdown">
                  {(() => {
                    const q = serviceFilterSearch.toLowerCase().trim();
                    const filtered = q ? activeServices.filter((s) => s.name.toLowerCase().includes(q)) : activeServices;
                    return filtered.length === 0 ? (
                      <div className="bms-cd-empty">{t("bms_no_data")}</div>
                    ) : filtered.slice(0, 8).map((s) => (
                      <button key={s.id} type="button" className="bms-cd-item" onClick={() => { setServiceFilterId(s.id); setServiceFilterSearch(""); setShowServiceFilterDropdown(false); }}>
                        <div className="bms-cd-info">
                          <div className="bms-cd-name">{s.name}</div>
                          <div className="bms-cd-meta">{s.duration_minutes} {t("bms_minutes_short")}</div>
                        </div>
                      </button>
                    ));
                  })()}
                </div>
              )}
            </div>

            <div className="bms-customer-search-wrap bms-filter-col" ref={customerFilterRef}>
              <div className="bms-customer-search-input">
                <TbSearch className="bms-cs-icon" />
                <input
                  type="text"
                  className="bms-cs-field"
                  placeholder={t("bms_customer_select_label")}
                  value={customerFilterId ? (() => {
                    const c = customers.find((x) => x.id === customerFilterId);
                    return c ? `${c.name} ${c.surname} (${c.phone})` : customerFilterSearch;
                  })() : customerFilterSearch}
                  onChange={(e) => {
                    setCustomerFilterSearch(e.target.value);
                    setCustomerFilterId(null);
                    setShowCustomerFilterDropdown(true);
                  }}
                  onFocus={() => setShowCustomerFilterDropdown(true)}
                />
                {customerFilterId && (
                  <button type="button" className="bms-cs-clear" onClick={() => { setCustomerFilterId(null); setCustomerFilterSearch(""); setShowCustomerFilterDropdown(true); }}><TbX size={14} /></button>
                )}
              </div>
              {showCustomerFilterDropdown && !customerFilterId && (
                <div className="bms-customer-dropdown">
                  {(() => {
                    const q = customerFilterSearch.toLowerCase().trim();
                    const filtered = q ? customers.filter((c) => `${c.name} ${c.surname}`.toLowerCase().includes(q) || c.phone?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)) : customers;
                    return filtered.length === 0 ? (
                      <div className="bms-cd-empty">{t("bms_customer_no_results")}</div>
                    ) : filtered.slice(0, 8).map((c) => (
                      <button key={c.id} type="button" className="bms-cd-item" onClick={() => { setCustomerFilterId(c.id); setCustomerFilterSearch(""); setShowCustomerFilterDropdown(false); }}>
                        <div className="bms-cd-avatar"><TbUser size={14} /></div>
                        <div className="bms-cd-info">
                          <div className="bms-cd-name">{c.name} {c.surname}</div>
                          <div className="bms-cd-meta">{c.phone}{c.email ? ` · ${c.email}` : ""}</div>
                        </div>
                      </button>
                    ));
                  })()}
                </div>
              )}
            </div>

            <div className="bms-filter-col bms-filter-date-wrap">
              <DateRangePicker
                startDate={dateFromFilter || undefined}
                endDate={dateToFilter || undefined}
                onChange={({ startDate, endDate }) => {
                  setDateFromFilter(startDate || "");
                  setDateToFilter(endDate || "");
                }}
                placeholder={t("date_range")}
                withPresets
                presets={dateRangePresets}
                leadingIcon={<TbCalendarEvent size={14} />}
                toggleVariant="outline-secondary"
                className="bms-date-range-filter"
                menuClassName="bms-date-range-filter-menu"
              />
            </div>
          </div>

          <div className="bms-bookings-filter-footer">
            <div className="bms-status-btn-group">
              <button type="button" className={`bms-status-btn ${statusFilter === "" ? "active" : ""}`} onClick={() => setStatusFilter("")}>
                <span className="bms-status-dot" /> {t("bms_all_status")}
              </button>
              {STATUS_OPTIONS_CREATE.map((s) => {
                const info = statusInfoCreate(s, t);
                return (
                  <button key={s} type="button" className={`bms-status-btn ${info.cls}${statusFilter === s ? " active" : ""}`} onClick={() => setStatusFilter(s)}>
                    <span className="bms-status-dot" /> {info.label}
                  </button>
                );
              })}
            </div>
            <div className="bms-bookings-filter-actions">
              <button className="bms-btn-outline" onClick={() => { setTextFilter(""); setServiceFilterId(null); setServiceFilterSearch(""); setCustomerFilterId(null); setCustomerFilterSearch(""); setDateFromFilter(""); setDateToFilter(""); setStatusFilter(""); }}>
                <TbX /> {t("clear")}
              </button>
              <button className="bms-btn-outline" onClick={load} disabled={!chatbotId}>
                <TbFilter /> {t("bms_filter")}
              </button>
            </div>
          </div>
        </div>

        <div className="bms-section-card mt-3">
          <div className="bms-section-header">
            <div className="bms-section-title">
              <span className="bms-section-icon"><TbCalendarEvent /></span>
              {t("bms_bookings")}
            </div>
            <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{bookingsTotal} {t("records")}</span>
          </div>
          {isLoading || loadingRefs ? (
            <div className="d-flex justify-content-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : bookings.length === 0 ? (
            <div className="bms-empty">
              <div className="bms-empty-icon"><TbCalendarEvent /></div>
              <p>{t("bms_no_data")}</p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table className="bms-table table mb-0">
                  <thead>
                    <tr>
                      <th>{t("bms_booking_no")}</th>
                      <th>{t("bms_status")}</th>
                      <th>{t("bms_resource")}</th>
                      <th>{t("bms_service")}</th>
                      <th>{t("bms_customer")}</th>
                      <th>{t("bms_phone")}</th>
                      <th>{t("bms_start")}</th>
                      <th>{t("bms_end")}</th>
                      <th style={{ width: 60, textAlign: "center" }}>{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((row) => (
                      <tr key={row.id}>
                        <td style={{ fontFamily: "var(--font-mono, monospace)", fontSize: "0.8rem", fontWeight: 600 }}>{row.booking_number}</td>
                        <td><span className={`bms-status ${STATUS_COLORS[row.status] || "active"}`}>{row.status}</span></td>
                        <td>{row.resource_name || row.resource_user_id}</td>
                        <td>{row.service_name}</td>
                        <td>{`${row.customer_name || ""} ${row.customer_surname || ""}`.trim() || "—"}</td>
                        <td style={{ fontFamily: "var(--font-mono, monospace)", fontSize: "0.8rem" }}>{row.customer_phone || "—"}</td>
                        <td style={{ whiteSpace: "nowrap", fontSize: "0.82rem" }}>{row.start_at}</td>
                        <td style={{ whiteSpace: "nowrap", fontSize: "0.82rem" }}>{row.end_at}</td>
                        <td style={{ textAlign: "center" }}>
                          <button type="button" className="bms-action-btn edit" title={t("edit")} onClick={() => openEdit(row)}>
                            <TbEdit />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <BmsPagination total={bookingsTotal} skip={skip} limit={limit} onPageChange={setSkip} onLimitChange={(v) => { setLimit(v); setSkip(0); }} />
            </>
          )}
        </div>
      </div>

      {/* Create Booking Modal */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} className="bms-modal" centered size="lg">
        <Modal.Header closeButton className="bms-modal-header">
          <Modal.Title className="d-flex align-items-center gap-2"><TbPlus /> {t("bms_new_booking")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Label className="bms-form-label">{t("bms_select_expert_booking")}</Form.Label>
              <Form.Select value={createForm.resource_user_id} onChange={(e) => setCreateForm((f) => ({ ...f, resource_user_id: Number(e.target.value) }))}>
                {activeUsers.map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
              </Form.Select>
            </div>
            <div className="col-md-6">
              <Form.Label className="bms-form-label">{t("bms_select_service_booking")}</Form.Label>
              <div className="bms-customer-search-wrap" ref={serviceSearchRefCreate}>
                <div className="bms-customer-search-input">
                  <TbSearch className="bms-cs-icon" />
                  <input
                    type="text"
                    className="bms-cs-field"
                    placeholder={t("bms_select_service_booking")}
                    value={createForm.service_id ? (() => {
                      const s = activeServices.find((x) => x.id === createForm.service_id);
                      return s ? `${s.name} (${s.duration_minutes} ${t("bms_minutes_short")})` : serviceSearchCreate;
                    })() : serviceSearchCreate}
                    onChange={(e) => {
                      setServiceSearchCreate(e.target.value);
                      setCreateForm((f) => ({ ...f, service_id: 0 }));
                      setShowServiceDropdownCreate(true);
                    }}
                    onFocus={() => setShowServiceDropdownCreate(true)}
                  />
                  {createForm.service_id > 0 && (
                    <button type="button" className="bms-cs-clear" onClick={() => { setCreateForm((f) => ({ ...f, service_id: 0 })); setServiceSearchCreate(""); setShowServiceDropdownCreate(true); }}>
                      <TbX size={14} />
                    </button>
                  )}
                </div>
                {showServiceDropdownCreate && !createForm.service_id && (
                  <div className="bms-customer-dropdown">
                    {(() => {
                      const q = serviceSearchCreate.toLowerCase().trim();
                      const filtered = q ? activeServices.filter((s) => s.name.toLowerCase().includes(q)) : activeServices;
                      return filtered.length === 0 ? (
                        <div className="bms-cd-empty">{t("bms_no_data")}</div>
                      ) : filtered.slice(0, 8).map((s) => (
                        <button key={s.id} type="button" className="bms-cd-item" onClick={() => {
                          setCreateForm((f) => ({ ...f, service_id: s.id }));
                          setServiceSearchCreate("");
                          setShowServiceDropdownCreate(false);
                        }}>
                          <div className="bms-cd-info">
                            <div className="bms-cd-name">{s.name}</div>
                            <div className="bms-cd-meta">{s.duration_minutes} {t("bms_minutes_short")}</div>
                          </div>
                        </button>
                      ));
                    })()}
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <Form.Label className="bms-form-label">{t("bms_booking_date")}</Form.Label>
              <Form.Control
                type="date"
                value={bookingDate}
                min={todayIso()}
                onChange={(e) => setBookingDate(e.target.value)}
              />
            </div>
            <div className="col-md-8">
              <Form.Label className="bms-form-label d-flex align-items-center justify-content-between">
                <span>{t("bms_select_slot")} ({slotDuration} {t("bms_minutes_short")})</span>
                {selectedStartMin !== null && (
                  <span className="bms-slot-selected-label">
                    <TbClock size={12} />{" "}
                    {formatTimeRange(selectedStartMin, selectedStartMin + slotDuration)}
                  </span>
                )}
              </Form.Label>
              <div className="bms-slot-legend">
                <span><i className="bms-slot-dot available" /> {t("bms_slot_available")}</span>
                <span><i className="bms-slot-dot booked" /> {t("bms_slot_booked")}</span>
                <span><i className="bms-slot-dot off" /> {t("bms_slot_off")}</span>
              </div>
              <div className="bms-slot-grid">
                {loadingSlots ? (
                  <div className="bms-slot-loading"><Spinner size="sm" animation="border" variant="primary" /></div>
                ) : slots.length === 0 ? (
                  <div className="bms-slot-empty">
                    {hasAvailability ? t("bms_no_data") : t("bms_slot_no_availability")}
                  </div>
                ) : (
                  slots.map((slot) => {
                    const isSelected = selectedStartMin === slot.startMin;
                    return (
                      <button
                        key={slot.startMin}
                        type="button"
                        className={`bms-slot-pill ${slot.status}${isSelected ? " selected" : ""}`}
                        onClick={() => handleSelectSlot(slot)}
                        disabled={slot.status !== "available"}
                        title={
                          slot.status === "available"
                            ? formatTimeRange(slot.startMin, slot.endMin)
                            : slot.status === "booked"
                            ? t("bms_slot_booked")
                            : t("bms_slot_off")
                        }
                      >
                        {slot.label}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
            {/* Status Buttons */}
            <div className="col-12">
              <Form.Label className="bms-form-label">{t("bms_status")}</Form.Label>
              <div className="bms-status-btn-group">
                {STATUS_OPTIONS_CREATE.map((s) => {
                  const info = statusInfoCreate(s, t);
                  return (
                    <button key={s} type="button" className={`bms-status-btn ${info.cls}${createStatus === s ? " active" : ""}`} onClick={() => setCreateStatus(s)}>
                      <span className="bms-status-dot" /> {info.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="col-12">
              <div className="bms-customer-section">
                <div className="bms-customer-header">
                  <Form.Label className="bms-form-label mb-0">{t("bms_customer_select_label")}</Form.Label>
                  <label className="bms-new-customer-toggle">
                    <input type="checkbox" checked={isNewCustomer} onChange={(e) => {
                      setIsNewCustomer(e.target.checked);
                      if (e.target.checked) { setSelectedCustomerId2(null); setCustomerSearch2(""); }
                      else { setCreateForm((f) => ({ ...f, customer_name: "", customer_surname: "", customer_phone: "", customer_email: "" })); }
                    }} />
                    <TbUserPlus size={14} />
                    <span>{t("bms_new_customer")}</span>
                  </label>
                </div>
                {!isNewCustomer ? (
                  <div className="bms-customer-search-wrap" ref={searchRef2}>
                    <div className="bms-customer-search-input">
                      <TbSearch className="bms-cs-icon" />
                      <input type="text" className="bms-cs-field" placeholder={t("bms_customer_search_placeholder")}
                        value={selectedCustomerId2 ? (() => { const c = customers.find((x) => x.id === selectedCustomerId2); return c ? `${c.name} ${c.surname} (${c.phone})` : customerSearch2; })() : customerSearch2}
                        onChange={(e) => { setCustomerSearch2(e.target.value); setSelectedCustomerId2(null); setShowDropdown2(true); }}
                        onFocus={() => setShowDropdown2(true)}
                      />
                      {selectedCustomerId2 && (
                        <button type="button" className="bms-cs-clear" onClick={() => { setSelectedCustomerId2(null); setCustomerSearch2(""); }}><TbX size={14} /></button>
                      )}
                    </div>
                    {showDropdown2 && !selectedCustomerId2 && (
                      <div className="bms-customer-dropdown">
                        {(() => {
                          const q = customerSearch2.toLowerCase();
                          const filtered = q ? customers.filter((c) => `${c.name} ${c.surname}`.toLowerCase().includes(q) || c.phone?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)) : customers;
                          return filtered.length === 0 ? (
                            <div className="bms-cd-empty">{t("bms_customer_no_results")}</div>
                          ) : filtered.slice(0, 8).map((c) => (
                            <button key={c.id} type="button" className="bms-cd-item" onClick={() => { setSelectedCustomerId2(c.id); setCustomerSearch2(""); setShowDropdown2(false); }}>
                              <div className="bms-cd-avatar"><TbUser size={14} /></div>
                              <div className="bms-cd-info">
                                <div className="bms-cd-name">{c.name} {c.surname}</div>
                                <div className="bms-cd-meta">{c.phone}{c.email ? ` · ${c.email}` : ""}</div>
                              </div>
                            </button>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bms-new-customer-form">
                    <div className="bms-ncf-hint"><TbUserPlus size={14} /> {t("bms_new_customer_hint")}</div>
                    <div className="row g-2">
                      <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_name")} *</Form.Label><Form.Control value={createForm.customer_name} onChange={(e) => setCreateForm((f) => ({ ...f, customer_name: e.target.value }))} /></div>
                      <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_surname")}</Form.Label><Form.Control value={createForm.customer_surname} onChange={(e) => setCreateForm((f) => ({ ...f, customer_surname: e.target.value }))} /></div>
                      <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_phone")} *</Form.Label><Form.Control value={createForm.customer_phone} onChange={(e) => setCreateForm((f) => ({ ...f, customer_phone: e.target.value }))} /></div>
                      <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_email")}</Form.Label><Form.Control type="email" value={createForm.customer_email} onChange={(e) => setCreateForm((f) => ({ ...f, customer_email: e.target.value }))} /></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="bms-modal-footer">
          <button className="bms-btn-secondary" onClick={() => setShowCreate(false)}><TbX /> {t("cancel")}</button>
          <button
            className="bms-btn-primary"
            onClick={handleCreate}
            disabled={saving || selectedStartMin === null}
          >
            {saving ? <Spinner size="sm" animation="border" /> : <TbCheck />} {t("save")}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Edit Booking Modal */}
      <Modal show={showEdit} onHide={() => { setShowEdit(false); setEditing(null); }} className="bms-modal" centered size="lg">
        <Modal.Header closeButton className="bms-modal-header">
          <Modal.Title className="d-flex align-items-center gap-2"><TbEdit /> {t("bms_edit_booking")}{editing ? ` — ${editing.booking_number}` : ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editing && (
            <div className="row g-3">
              <div className="col-md-6">
                <Form.Label className="bms-form-label">{t("bms_status")}</Form.Label>
                <div className="bms-status-btn-group">
                  {STATUS_OPTIONS_CREATE.map((s) => {
                    const info = statusInfoCreate(s, t);
                    return (
                      <button
                        key={s}
                        type="button"
                        className={`bms-status-btn ${info.cls}${editForm.status === s ? " active" : ""}`}
                        onClick={() => setEditForm((f) => ({ ...f, status: s }))}
                      >
                        <span className="bms-status-dot" /> {info.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_customer")}</Form.Label>
                <div className="bms-customer-section">
                  <div className="bms-customer-header">
                    <Form.Label className="bms-form-label mb-0">{t("bms_customer_select_label")}</Form.Label>
                    <label className="bms-new-customer-toggle">
                      <input type="checkbox" checked={editIsNewCustomer} onChange={(e) => {
                        setEditIsNewCustomer(e.target.checked);
                        if (e.target.checked) {
                          setEditSelectedCustomerId(null);
                          setEditCustomerSearch("");
                        } else {
                          setEditCustomerName("");
                          setEditCustomerSurname("");
                          setEditCustomerPhone("");
                          setEditCustomerEmail("");
                        }
                      }} />
                      <TbUserPlus size={14} />
                      <span>{t("bms_new_customer")}</span>
                    </label>
                  </div>
                  {!editIsNewCustomer ? (
                    <div className="bms-customer-search-wrap">
                      <div className="bms-customer-search-input">
                        <TbSearch className="bms-cs-icon" />
                        <input
                          type="text"
                          className="bms-cs-field"
                          placeholder={t("bms_customer_search_placeholder")}
                          value={editSelectedCustomerId ? (() => {
                            const c = customers.find((x) => x.id === editSelectedCustomerId);
                            return c ? `${c.name} ${c.surname} (${c.phone})` : editCustomerSearch;
                          })() : editCustomerSearch}
                          onChange={(e) => {
                            setEditCustomerSearch(e.target.value);
                            setEditSelectedCustomerId(null);
                            setEditShowDropdown(true);
                          }}
                          onFocus={() => setEditShowDropdown(true)}
                        />
                        {editSelectedCustomerId && (
                          <button type="button" className="bms-cs-clear" onClick={() => { setEditSelectedCustomerId(null); setEditCustomerSearch(""); }}>
                            <TbX size={14} />
                          </button>
                        )}
                      </div>
                      {editShowDropdown && !editSelectedCustomerId && (
                        <div className="bms-customer-dropdown">
                          {(() => {
                            const q = editCustomerSearch.toLowerCase();
                            const filtered = q ? customers.filter((c) => `${c.name} ${c.surname}`.toLowerCase().includes(q) || c.phone?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)) : customers;
                            return filtered.length === 0 ? (
                              <div className="bms-cd-empty">{t("bms_customer_no_results")}</div>
                            ) : filtered.slice(0, 8).map((c) => (
                              <button key={c.id} type="button" className="bms-cd-item" onClick={() => {
                                setEditSelectedCustomerId(c.id);
                                setEditForm((f) => ({ ...f, customer_id: c.id }));
                                setEditCustomerSearch("");
                                setEditShowDropdown(false);
                              }}>
                                <div className="bms-cd-avatar"><TbUser size={14} /></div>
                                <div className="bms-cd-info">
                                  <div className="bms-cd-name">{c.name} {c.surname}</div>
                                  <div className="bms-cd-meta">{c.phone}{c.email ? ` · ${c.email}` : ""}</div>
                                </div>
                              </button>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bms-new-customer-form">
                      <div className="row g-2">
                        <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_name")} *</Form.Label><Form.Control value={editCustomerName} onChange={(e) => setEditCustomerName(e.target.value)} /></div>
                        <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_surname")}</Form.Label><Form.Control value={editCustomerSurname} onChange={(e) => setEditCustomerSurname(e.target.value)} /></div>
                        <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_phone")} *</Form.Label><Form.Control value={editCustomerPhone} onChange={(e) => setEditCustomerPhone(e.target.value)} /></div>
                        <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_email")}</Form.Label><Form.Control type="email" value={editCustomerEmail} onChange={(e) => setEditCustomerEmail(e.target.value)} /></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_resource")}</Form.Label>
                <Form.Select value={editForm.resource_user_id} onChange={(e) => setEditForm((f) => ({ ...f, resource_user_id: Number(e.target.value) }))}>{activeUsers.map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}</Form.Select>
              </div>
              <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_service")}</Form.Label>
                <div className="bms-customer-search-wrap" ref={editServiceSearchRef}>
                  <div className="bms-customer-search-input">
                    <TbSearch className="bms-cs-icon" />
                    <input
                      type="text"
                      className="bms-cs-field"
                      placeholder={t("bms_select_service_booking")}
                      value={editForm.service_id ? (() => {
                        const s = activeServices.find((x) => x.id === editForm.service_id);
                        return s ? `${s.name} (${s.duration_minutes} ${t("bms_minutes_short")})` : editServiceSearch;
                      })() : editServiceSearch}
                      onChange={(e) => {
                        setEditServiceSearch(e.target.value);
                        setEditForm((f) => ({ ...f, service_id: 0 }));
                        setEditShowServiceDropdown(true);
                      }}
                      onFocus={() => setEditShowServiceDropdown(true)}
                    />
                    {editForm.service_id > 0 && (
                      <button type="button" className="bms-cs-clear" onClick={() => { setEditForm((f) => ({ ...f, service_id: 0 })); setEditServiceSearch(""); setEditShowServiceDropdown(true); }}>
                        <TbX size={14} />
                      </button>
                    )}
                  </div>
                  {editShowServiceDropdown && !editForm.service_id && (
                    <div className="bms-customer-dropdown">
                      {(() => {
                        const q = editServiceSearch.toLowerCase().trim();
                        const filtered = q ? activeServices.filter((s) => s.name.toLowerCase().includes(q)) : activeServices;
                        return filtered.length === 0 ? (
                          <div className="bms-cd-empty">{t("bms_no_data")}</div>
                        ) : filtered.slice(0, 8).map((s) => (
                          <button key={s.id} type="button" className="bms-cd-item" onClick={() => {
                            setEditForm((f) => ({ ...f, service_id: s.id }));
                            setEditServiceSearch("");
                            setEditShowServiceDropdown(false);
                          }}>
                            <div className="bms-cd-info">
                              <div className="bms-cd-name">{s.name}</div>
                              <div className="bms-cd-meta">{s.duration_minutes} {t("bms_minutes_short")}</div>
                            </div>
                          </button>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_start")}</Form.Label><Form.Control type="datetime-local" value={editForm.start_at} onChange={(e) => setEditForm((f) => ({ ...f, start_at: e.target.value }))} /></div>
              <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_end")}</Form.Label><Form.Control type="datetime-local" value={editForm.end_at} onChange={(e) => setEditForm((f) => ({ ...f, end_at: e.target.value }))} /></div>
              <div className="col-md-6"><Form.Label className="bms-form-label">{t("bms_timezone")}</Form.Label><Form.Control value={editForm.timezone} onChange={(e) => setEditForm((f) => ({ ...f, timezone: e.target.value }))} /></div>
              {editForm.status === "cancelled" && (
                <div className="col-12"><Form.Label className="bms-form-label">{t("bms_cancellation_reason")}</Form.Label><Form.Control as="textarea" rows={2} value={editForm.cancellation_reason} onChange={(e) => setEditForm((f) => ({ ...f, cancellation_reason: e.target.value }))} /></div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bms-modal-footer">
          <button className="bms-btn-secondary" onClick={() => { setShowEdit(false); setEditing(null); }}><TbX /> {t("cancel")}</button>
          <button className="bms-btn-primary" onClick={handleEditSave} disabled={saving || !editing}>{saving ? <Spinner size="sm" animation="border" /> : <TbCheck />} {t("save")}</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BmsBookingsPage;
